import { Container, Sprite, type Text, type Texture } from "pixi.js";
import type { PresentationTaskRunner, ViewportStore } from "@slot/presentation";
import { ModalChrome, bindTap } from "@slot/game-shell";
import type { FeatureFinishedPort, LocalizationService } from "@slot/game-shell";
import { CandyPanelMotion, type CandyPanelMotionTuning } from "../CandyPanelMotion";
import { CANDY_TEXT, candyLabel, candyValue } from "../candyText";

/** The pink candy card (the art's 758×996 at 760 wide) with the session's total on the card's cream. */
const GEOMETRY = {
  content: { width: 900, height: 1160, y: { desktop: 0, mobile: -150, mobile_landscape: 0 } },
  card: { width: 760, height: 998 },
  title: { y: -170, fontSize: 82, box: { width: 560, height: 110 } },
  amount: { y: 40, fontSize: 130, box: { width: 600, height: 170 } },
  subtitle: { y: 230, fontSize: 70, box: { width: 560, height: 100 } },
} as const;

/**
 * The free-spins summary behind `FeatureFinishedPort`: "You won / <total> / in free spins" on the pink card, dismissed by
 * a tap. The title has no extra-spins offer surface (`hasOffer` false), so the shell never asks for the offer layout.
 */
export class FeatureFinishedView extends Container implements FeatureFinishedPort {
  public readonly hasOffer = false;
  private readonly chrome: ModalChrome;
  private readonly motion: CandyPanelMotion;
  private readonly summaryLabel: Text;

  public constructor(input: { card: Texture | null; viewport: ViewportStore; localization: LocalizationService; runner: PresentationTaskRunner; motion: CandyPanelMotionTuning }) {
    super({ label: "FeatureFinishedPanel" });
    const g = GEOMETRY;
    this.chrome = new ModalChrome({
      label: "simpleRoot",
      viewport: input.viewport,
      localization: input.localization,
      runner: input.runner,
      content: { ...g.content, limits: { desktop: { maxHeight: 1500 }, mobile: { maxWidth: 1000 }, mobile_landscape: {} } },
    });
    this.addChild(this.chrome);

    const panel = new Container({ label: "Panel" });
    if (input.card) {
      const card = new Sprite({ texture: input.card, anchor: 0.5, label: "Card" });
      card.setSize(g.card.width, g.card.height);
      panel.addChild(card);
    }
    this.summaryLabel = candyValue({ style: CANDY_TEXT.amount, fontSize: g.amount.fontSize, y: g.amount.y, label: "AmountLabel" });
    panel.addChild(
      candyLabel({ localization: input.localization, key: "featurefinished.title", fallback: "You Won", style: CANDY_TEXT.heading, ...g.title, label: "Title" }),
      this.summaryLabel,
      candyLabel({ localization: input.localization, key: "featurefinished.subtitle", fallback: "in Free Spins", style: CANDY_TEXT.body, ...g.subtitle, label: "SubTitle" }),
    );
    this.chrome.content.addChild(panel);
    this.motion = new CandyPanelMotion({ chrome: this.chrome, panel, runner: input.runner, tuning: input.motion });
  }

  public showSimple(): void {
    this.motion.preSeat();
    this.chrome.visible = true;
  }

  public showOffer(): void {
    this.showSimple();
  }

  public hide(): void {
    this.chrome.visible = false;
    this.motion.stop();
  }

  public setSummary(text: string): void {
    this.summaryLabel.text = text ?? "";
    const { box } = GEOMETRY.amount;
    const scale = Math.min(1, box.width / Math.max(1, this.summaryLabel.width / this.summaryLabel.scale.x));
    this.summaryLabel.scale.set(scale, scale);
  }

  public setCostText(_text: string): void {}

  public setSkipEnabled(enabled: boolean): void {
    this.chrome.skipButton.visible = enabled;
  }

  public setBuyEnabled(_enabled: boolean): void {}

  public setDeclineEnabled(_enabled: boolean): void {}

  public bindSkipTap(onPress: () => void): () => void {
    return bindTap(this.chrome.skipButton, onPress);
  }

  public bindBuyTap(_onPress: () => void): () => void {
    return () => {};
  }

  public bindDeclineTap(_onPress: () => void): () => void {
    return () => {};
  }

  public playEnterAnimation(): Promise<void> {
    return this.motion.enter();
  }

  public playExitAnimation(): Promise<void> {
    return this.motion.exit();
  }

  public fadeInContinueText(): Promise<void> {
    return this.motion.fadeInContinueText();
  }

  public dispose(): void {
    this.chrome.dispose();
    this.destroy({ children: true });
  }
}
